import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';

const ElementChart = ({ elements, scores, userScores, showUserScores, onElementClick, selectedElement }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || elements.length === 0) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }
    
    const chart = chartInstance.current;

    const indicators = elements.map(element => ({
      name: element.title || 'Unbenannt',
      max: 7,
      color: '#152627'
    }));

    const dataValues = elements.map(element => scores[element.id] || 0);
    const userDataValues = elements.map(element => userScores?.[element.id] || 0);

    const option = {
      triggerEvent: true, // IMPORTANT: Enables clicking on axis labels
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        textStyle: {
          color: '#15191a'
        },
        formatter: (params) => {
          let result = `<div class="font-bold mb-2 text-[#152627] border-b pb-1 border-gray-100">${params.name}</div>`;
          elements.forEach((el, index) => {
            // Team Score
            const teamScore = scores[el.id] || 0;
            // User Score (only if active)
            const userScore = userScores?.[el.id] || 0;
            
            result += `<div class="flex justify-between items-center min-w-[200px] mb-1">
              <span class="mr-4 text-gray-600 text-sm">${el.title}:</span>
              <div class="flex items-center space-x-3">
                 ${showUserScores ? `<span style="color: #87ff8f; font-weight: bold;">${userScore}</span>` : ''}
                 <b style="color: #152627;">${teamScore}</b>
              </div>
            </div>`;
          });
          return result;
        }
      },
      radar: {
        indicator: indicators,
        shape: 'polygon',
        radius: '65%',
        center: ['50%', '50%'], // Centered in the sized canvas
        splitNumber: 7,
        axisName: {
          fontSize: 11,
          fontWeight: 600,
          padding: [3, 5],
          color: '#152627',
          triggerEvent: true // Explicitly enable events for axis names
        },
        splitArea: {
          areaStyle: {
            color: ['#f8fafc', '#f1f5f9', '#e2e8f0', '#cbd5e1', '#94a3b8', '#64748b', '#475569'].reverse().slice(0, 7),
            shadowColor: 'rgba(0, 0, 0, 0.1)',
            shadowBlur: 5
          }
        },
        axisLine: {
          lineStyle: {
            color: 'rgba(21, 38, 39, 0.2)'
          }
        },
        splitLine: {
          lineStyle: {
            color: 'rgba(21, 38, 39, 0.2)'
          }
        }
      },
      series: [
        {
          name: 'Bewertung',
          type: 'radar',
          data: [
            {
              value: dataValues,
              name: 'Team Durchschnitt',
              symbol: 'circle',
              symbolSize: 6,
              itemStyle: {
                color: '#152627',
                borderColor: '#152627',
                borderWidth: 2
              },
              lineStyle: {
                width: 3,
                color: '#152627'
              },
              areaStyle: {
                color: 'rgba(21, 38, 39, 0.35)' // Darker, distinct fill for team
              }
            },
            ...(showUserScores ? [{
              value: userDataValues,
              name: 'Meine Bewertung',
              symbol: 'circle',
              symbolSize: 6,
              itemStyle: {
                color: '#87ff8f', // Distinct Blue
                borderColor: '#87ff8f',
                borderWidth: 2
              },
              lineStyle: {
                width: 3,
                color: '#87ff8f',
                type: 'solid' // Solid line for better visibility
              },
              areaStyle: {
                color: 'rgba(59, 130, 246, 0.15)' // Light blue fill
              }
            }] : [])
          ]
        }
      ]
    };

    chart.setOption(option);

    const handleChartClick = (params) => {
      // 1. Check for click on Axis Name (Label)
      if (params.componentType === 'radar' && params.name) {
          const clickedElement = elements.find(el => el.title === params.name);
          if (clickedElement) {
              onElementClick(clickedElement);
              return;
          }
      } 
      
      // 2. Check for click via targetType (ECharts specific property for axis labels)
      if (params.targetType === 'axisName' && params.name) {
         const clickedElement = elements.find(el => el.title === params.name);
         if (clickedElement) {
             onElementClick(clickedElement);
             return;
         }
      }
    };
    
    chart.on('click', handleChartClick);

    const handleResize = () => {
      chart.resize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      chart.off('click', handleChartClick);
      window.removeEventListener('resize', handleResize);
      chart.dispose();
      chartInstance.current = null;
    };
  }, [elements, scores, userScores, showUserScores, onElementClick]);

  return (
    <div className="flex flex-col items-center w-full justify-start">
      <div 
        ref={chartRef} 
        style={{ width: '100%', height: '450px', cursor: 'pointer' }}
      />
      
      {/* Legend */}
      <div className="flex items-center justify-center space-x-6 mt-2 mb-3">
        <div className="flex items-center">
          <span className="w-3 h-3 rounded-full bg-[#152627] mr-2 shadow-sm border border-gray-200"></span>
          <span className="text-xs font-medium text-gray-600">Team Durchschnitt</span>
        </div>
        {showUserScores && (
          <div className="flex items-center animate-fadeIn">
            <span className="w-3 h-3 rounded-full bg-[#87ff8f] mr-2 shadow-sm border border-blue-200"></span>
            <span className="text-xs font-medium text-gray-600">Meine Bewertung</span>
          </div>
        )}
      </div>

      <div className="text-xs text-gray-400 italic">
        Klicken Sie auf die Beschriftungen im Diagramm, um Details anzuzeigen.
      </div>
    </div>
  );
};

export default ElementChart;